import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AnyAction } from '@reduxjs/toolkit';
import {
    selectedPrescripteursSelector,
    SousClient,
    SousClientApi,
    SousClientsSelector,
    useFormaliteService,
    useSociete,
    useTiersService,
    useTiersServiceSelector,
} from '@europrocurement/l2d-domain';
import {
    ActionButton,
    ActionButtonProps,
    ChoiceItemProps,
    ChoiceList,
    ModalContext,
} from '@europrocurement/flexy-components';
import {
    AdresseJsonldTiersReadNoteGroupTimestampableBlameambleGroup,
    TiersSousclientJsonldSousclientCreated,
} from '@europrocurement/l2d-domain/openApi/ApiTiers';
import { useDebouncedCallback } from 'use-debounce';
import { plusIcon } from '@europrocurement/l2d-icons';
import { PrescriberClientForm } from '@europrocurement/l2d-modules/modules/PrescriberClient/components/AddPrescriberClient';
import { enqueueSnackbar } from 'notistack';
import { Typography } from '@mui/material';
import { addressToString } from '@europrocurement/l2d-utils';
import { IMMATRICULATION_ID } from '../../constants';

export type SelectSocieteProps = {
    onSelect?: (ids: number[], sousClients: SousClientApi[]) => void;
    multiple?: boolean;
    back?: boolean;
    title?: string;
    description?: string;
    societeSelectedDefault?: string[];
    submitButtonLabel?: string;
    idFormalites: Array<number>;
};

const selectAddressFacturante = (
    addresses: AdresseJsonldTiersReadNoteGroupTimestampableBlameambleGroup[],
) => addresses.filter((adresse) => adresse.facturation === true);

const SelectSociete: React.FunctionComponent<SelectSocieteProps> = function ({
    onSelect,
    back = false,
    multiple = false,
    title = 'Pour quelle société est destinée la formalité ?',
    description = '',
    societeSelectedDefault = [],
    submitButtonLabel = 'Continuer',
    idFormalites = [],
}) {
    const datasource = useTiersServiceSelector(SousClientsSelector).main;
    const dispatch = useDispatch();
    const [sousClients, setSousClients] = useState<Array<SousClient>>([]);
    const prescripteur = useTiersServiceSelector(selectedPrescripteursSelector);
    const [listFiltered, setListFiltered] = useState<Array<ChoiceItemProps>>([]); // liste filtré par la fonction search
    const [listPrescripteur, setListPrescripteur] = useState<Array<ChoiceItemProps>>([]); // Liste de société du prescripteur
    const [societeSelected, setSocieteSelected] = useState<Array<string>>(societeSelectedDefault); // tableau d'id sélectionné
    const [isImmatriculation, setIsImmatriculation] = useState<boolean>(false);
    const { modalActions } = React.useContext(ModalContext);
    const { sousClientsApi, getSousClients } = useTiersService();
    const { formaliteApi } = useFormaliteService();
    const xIdSociete = useSociete();

    const updateIsImmatriculation = useCallback(async () => {
        const promises = (idFormalites || []).map((idFormalite) =>
            formaliteApi.apiFormalitesIdGet({
                id: idFormalite as unknown as string,
            }),
        );
        const results = await Promise.all(promises);
        const withImmat = results.find(
            (response) => response.data.categorie?.id === IMMATRICULATION_ID,
        );
        setIsImmatriculation(withImmat !== undefined);
    }, [idFormalites, formaliteApi, setIsImmatriculation]);

    if (!prescripteur || prescripteur.id === undefined) {
        throw new Error('no prescriber');
    }

    // suppression de la recherche à l'entrée du formulaire et sortie de la page
    useEffect(() => {
        dispatch({
            type: 'sousClient/resetmainSearch',
        });

        return () => {
            dispatch({
                type: 'sousClient/resetmainSearch',
            });
            dispatch({
                type: 'sousClient/resetmainFilter',
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (datasource.status === 'succeeded') {
            setSousClients(datasource.data);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [datasource.status]);

    // initialisation liste Prescripteur/Societé
    useEffect(() => {
        if (!prescripteur || !prescripteur.id) {
            return;
        }
        dispatch({
            type: 'sousClient/setmainFilter',
            payload: {
                key: 'prescripteurId',
                value: prescripteur.id,
            },
        });
        dispatch(getSousClients({}) as unknown as AnyAction);
        const prescripteurPrincipal: ChoiceItemProps = {
            label: prescripteur.raisonSociale,
            value: prescripteur.id?.toString(),
            idFiscal: prescripteur.siren ? `Siren : ${prescripteur.siren}` : '',
        };
        const address = selectAddressFacturante(prescripteur.adresses);
        if (address.length > 0) {
            Object.assign(prescripteurPrincipal, { description: addressToString(address[0]) });
        }
        setListPrescripteur([prescripteurPrincipal]);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // MAJ sousclient en fonction du datasource etc...
    useEffect(() => {
        if (datasource.status === 'succeeded') {
            const listSC: ChoiceItemProps[] = [];
            sousClients.forEach((sclient) => {
                if (sclient.id === undefined) {
                    return;
                }
                const obj: ChoiceItemProps = {
                    label: sclient.raisonSociale,
                    value: sclient.id.toString(),
                };

                // Récupère le siren en priorité ou le tvaIntracommunautaire
                if (sclient.siren && sclient.siren.trim() !== '') {
                    Object.assign(obj, { idFiscal: `Siren : ${sclient.siren}` });
                } else if (
                    sclient.tvaIntracommunautaire &&
                    sclient.tvaIntracommunautaire.trim() !== ''
                ) {
                    Object.assign(obj, {
                        idFiscal: `Numero d’identification fiscal : ${sclient.tvaIntracommunautaire}`,
                    });
                }

                if (sclient.siren !== '') {
                    Object.assign(obj, { siren: sclient.siren });
                }
                const address = selectAddressFacturante(sclient.adresses);
                if (address.length > 0) {
                    Object.assign(obj, { description: addressToString(address[0]) });
                }

                listSC.push(obj);
            });
            setListFiltered(listSC);
        }
    }, [datasource, sousClients]);

    // va rechercher au changement de societeselected
    useEffect(() => {
        dispatch({
            type: 'sousClient/setmainFilter',
            payload: {
                key: 'orSousClientId',
                value: societeSelected,
            },
        });
        dispatch(getSousClients({}) as unknown as AnyAction);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, societeSelected]);

    useEffect(() => {
        updateIsImmatriculation();
    }, [updateIsImmatriculation]);

    const onSubmitWrapper = useCallback(
        (data: { idsSociete: Array<string> }) => {
            const arrayIds = data.idsSociete;
            if (onSelect) {
                onSelect(
                    arrayIds.map((str: string) => Number(str)),
                    [...sousClients, prescripteur].filter(
                        (sousClient) => arrayIds.indexOf(`${sousClient.id}`) !== -1,
                    ),
                );
            }
        },
        [onSelect, sousClients, prescripteur],
    );

    // APPEL OPENSEARCH au changement du filtre
    const onSearchWrapper = useDebouncedCallback((currentSearchString: string) => {
        if (currentSearchString !== '') {
            dispatch({
                type: 'sousClient/setmainSearch',
                payload: {
                    search: currentSearchString,
                },
            });
            dispatch(getSousClients({}) as unknown as AnyAction);
        } else {
            dispatch({
                type: 'sousClient/resetmainSearch',
            });
            dispatch(getSousClients({}) as unknown as AnyAction);
        }
    }, 500);

    const onChangeWrapper = (arraySelected: string[]) => {
        setSocieteSelected(arraySelected);
    };

    const handleSuccess = (sousClient: TiersSousclientJsonldSousclientCreated) => {
        modalActions.close();
        // attente 1 seconde pour récupération du sous-client
        setTimeout(() => {
            dispatch({
                type: 'sousClient/setmainPagination',
                payload: {
                    itemsPerPage: 500,
                    page: 0,
                },
            });
            dispatch({
                type: 'sousClient/setmainFilter',
                payload: {
                    key: 'prescripteurId',
                    value: prescripteur.id,
                },
            });
            dispatch(getSousClients({}) as unknown as AnyAction);
        }, 1000);

        if (sousClient.id !== undefined) {
            const value = sousClient.id.toString();
            setSocieteSelected((currentUpdatedValues) => {
                let updatedValues: Array<string>;
                if (currentUpdatedValues.includes(value)) {
                    updatedValues = currentUpdatedValues.filter((v) => v !== value); // Déselectionne si déjà sélectionné
                } else if (multiple) {
                    updatedValues = [...currentUpdatedValues, value]; // ajout multiple
                } else {
                    updatedValues = [value]; // ajout simple
                }
                return updatedValues;
            });

            if (onSelect) {
                sousClientsApi
                    .getTiersTiersItem({
                        id: `${sousClient.id}`,
                        xIdSociete,
                    })
                    .then((res) => onSelect([sousClient.id], [res.data]));
            }
        }
    };
    const handleCancel = () => {
        modalActions.close();
    };

    const handleUpdateItem = (value: string | number) => {
        const clientToUpdate = sousClients.find((sousClient) => sousClient.id === Number(value));
        if (clientToUpdate !== undefined) {
            modalActions.call(
                <PrescriberClientForm
                    onCancel={handleCancel}
                    onSuccess={handleSuccess}
                    sousClient={clientToUpdate}
                    sirenRequired={false}
                />,
            );
        } else {
            enqueueSnackbar(
                <Typography>
                    Une erreur est survenue lors de la récupération de la société.
                </Typography>,
                { variant: 'error' },
            );
        }
    };

    const actionBtnAdd: ActionButtonProps = {
        label: 'Ajouter une société',
        icon: plusIcon,
        action: () =>
            modalActions.call(
                <PrescriberClientForm
                    onCancel={handleCancel}
                    onSuccess={handleSuccess}
                    sirenRequired={!isImmatriculation}
                    showCompanyIdentifier={!isImmatriculation}
                />,
            ),
        sx: {
            display: 'flex',
            alignItems: 'center',
            padding: '8px 14px',
            gap: '12px',
            borderRadius: '12px',
            width: '100%',
            justifyContent: 'flex-start',
            fontFamily: 'Inter',
            fontSize: '15px',
            fontWeight: '600',
            lineHeight: '24px',
            color: 'text.secondary',
            backgroundColor: 'inherit',
            margin: '0',
            transition: 'background-color ease-in-out 300ms',
            '&:hover': {
                backgroundColor: 'text.gris005',
            },
        },
    };

    return (
        <ChoiceList
            title={title}
            description={description}
            submitButtonLabel={submitButtonLabel}
            placeholderLabelSearch="Nom ou Siren de la société concernée"
            listChoice={[
                {
                    label: back ? 'Clients du prescripteur' : 'Mes clients',
                    listChoice: listFiltered,
                    multiple,
                    topSelected: true,
                    updateItem: handleUpdateItem,
                },
                {
                    label: back ? 'Le prescripteur' : 'Ma société',
                    listChoice: listPrescripteur,
                    multiple,
                },
            ]}
            multiple={multiple}
            onSubmit={onSubmitWrapper}
            search={onSearchWrapper}
            onChange={onChangeWrapper}
            componentAdd={<ActionButton {...actionBtnAdd} />}
            defaultValues={societeSelected}
            invisibleCheckbox
        />
    );
};

export default SelectSociete;
